import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";

// Share a charger's location: a Google Maps link plus its address, by copying it,
// the device's share sheet (phones), WhatsApp or email. The link uses the saved
// coordinates when there are any (exact pin), else searches the address.

const hasCoord = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

export const googleMapsLink = ({ lat, lng, address }) => {
  if (hasCoord(lat) && hasCoord(lng) && !(Number(lat) === 0 && Number(lng) === 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${Number(lat)},${Number(lng)}`;
  }
  return address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;
};

const ShareLocationButton = ({ name, address, lat, lng }) => {
  const dispatch = useDispatch();
  const [anchor, setAnchor] = useState(null);
  const link = googleMapsLink({ lat, lng, address });
  const text = [name, address, link].filter(Boolean).join("\n");
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const notify = (message, severity = "success") =>
    dispatch(alertSlice.actions.setAlert({ open: true, message, severity }));
  const close = () => setAnchor(null);

  const copy = async () => {
    close();
    try {
      await navigator.clipboard.writeText(text);
      notify("Location copied — paste it anywhere to share");
    } catch (_) {
      notify("Couldn't copy. Select and copy the link from the address bar instead.", "error");
    }
  };
  const shareNative = async () => {
    close();
    try {
      await navigator.share({ title: name || "Charger location", text: [name, address].filter(Boolean).join("\n"), url: link });
    } catch (_) {
      /* the user dismissed the share sheet */
    }
  };
  const open = (url) => {
    close();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Tooltip title={link ? "Share a Google Maps link with the address" : "Set the location or address first"}>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon icon="eva:share-outline" width={18} height={18} />}
            disabled={!link}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            Share location
          </Button>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem onClick={copy}>
          <ListItemIcon>
            <Icon icon="eva:copy-outline" width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="Copy link and address" />
        </MenuItem>
        {canShare && (
          <MenuItem onClick={shareNative}>
            <ListItemIcon>
              <Icon icon="eva:share-outline" width={20} height={20} />
            </ListItemIcon>
            <ListItemText primary="Share…" />
          </MenuItem>
        )}
        <MenuItem onClick={() => open(`https://wa.me/?text=${encodeURIComponent(text)}`)}>
          <ListItemIcon>
            <Icon icon="logos:whatsapp-icon" width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="WhatsApp" />
        </MenuItem>
        <MenuItem
          onClick={() =>
            open(
              `mailto:?subject=${encodeURIComponent(`Location: ${name || "charger"}`)}&body=${encodeURIComponent(text)}`
            )
          }
        >
          <ListItemIcon>
            <Icon icon="eva:email-outline" width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="Email" />
        </MenuItem>
        <MenuItem onClick={() => open(link)}>
          <ListItemIcon>
            <Icon icon="eva:external-link-outline" width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="Open in Google Maps" />
        </MenuItem>
      </Menu>
    </>
  );
};

ShareLocationButton.propTypes = {
  name: PropTypes.string,
  address: PropTypes.string,
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ShareLocationButton;
